import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Gift, Copy, Check, ArrowRight, Flame } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { submitLead, setStoredPromo } from "@/lib/promos";

export function ClaimPopup({ open, onOpenChange, deal }) {
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "" });
    const [loading, setLoading] = useState(false);
    const [claimed, setClaimed] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => { if (open) { setClaimed(null); setCopied(false); } }, [open]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { deal: d } = await submitLead(form.name, form.email);
            setClaimed(d);
            setStoredPromo(d.code);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not claim — check your name and email.");
        } finally {
            setLoading(false);
        }
    };

    const active = claimed || deal;
    const code = active?.code || "";

    const claimNow = () => {
        setStoredPromo(code);
        onOpenChange(false);
        navigate(`/register?code=${encodeURIComponent(code)}`);
    };

    const copy = () => {
        navigator.clipboard?.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="rounded-sm border border-volt/30 bg-obsidian/95 backdrop-blur-2xl ring-1 ring-white/5 text-white sm:rounded-sm"
                data-testid="claim-popup">
                {!claimed ? (
                    <>
                        <DialogHeader>
                            <div className="w-11 h-11 rounded-sm bg-volt/15 border border-volt/40 grid place-items-center mb-2">
                                <Gift className="w-5 h-5 text-volt" />
                            </div>
                            <DialogTitle className="font-heading text-2xl font-bold">Claim your daily deal</DialogTitle>
                            <DialogDescription className="text-muted-foreground">
                                {active ? <>Drop your name and email to unlock <span className="text-volt font-semibold">{active.desc}</span>.</> : "Enter your details to unlock today's code."}
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-4 mt-1" data-testid="claim-form">
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
                                <Input data-testid="email-popup-input-name" required value={form.name} onChange={set("name")}
                                    placeholder="Alex Rivera" className="bg-surface border-border rounded-sm h-11" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                                <Input data-testid="email-popup-input-email" type="email" required value={form.email} onChange={set("email")}
                                    placeholder="you@example.com" className="bg-surface border-border rounded-sm h-11" />
                            </div>
                            <button type="submit" disabled={loading} data-testid="email-popup-submit"
                                className="w-full h-11 rounded-sm bg-volt text-black font-bold uppercase tracking-wide hover:bg-[#33ceff] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" />} Reveal My Code
                            </button>
                            <p className="text-[11px] text-muted-foreground text-center">No spam. Your code applies automatically at checkout.</p>
                        </form>
                    </>
                ) : (
                    <div className="text-center" data-testid="claim-success">
                        <DialogHeader>
                            <DialogTitle className="font-heading text-2xl font-bold">Your code is locked in</DialogTitle>
                            <DialogDescription className="text-muted-foreground">{claimed.desc} — applied automatically at checkout.</DialogDescription>
                        </DialogHeader>
                        <button onClick={copy} data-testid="email-popup-copy"
                            className="mx-auto my-5 flex items-center gap-3 px-5 py-3 rounded-sm border border-dashed border-volt/50 bg-volt/[0.06] hover:bg-volt/10 transition-colors">
                            <span className="font-mono text-2xl font-bold tracking-[0.15em] text-volt">{code}</span>
                            {copied ? <Check className="w-5 h-5 text-success" /> : <Copy className="w-5 h-5 text-muted-foreground" />}
                        </button>
                        <button onClick={claimNow} data-testid="email-popup-claim-code"
                            className="w-full h-12 rounded-sm bg-volt text-black font-bold uppercase tracking-wide hover:bg-[#33ceff] transition-colors flex items-center justify-center gap-2">
                            Claim Now <ArrowRight className="w-4 h-4" />
                        </button>
                        <p className="text-[11px] text-muted-foreground mt-3">Takes you to create your account and start a 7-day free trial.</p>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
